import { ApiError } from "@/lib/api-client";
import { contractDateMessage } from "@/routes/admin/contract-date-bounds";

// Story 15.1 AC14 — the ONE mapper every refusal on Screen 33 reaches the operator
// through. Follows the shipped `admin-user-error.ts` convention exactly: branch on
// `ApiError.code` / `ApiError.status` / a 422 `loc`, order the coded branches ahead
// of the bare statuses, and NEVER branch on the server's raw English `msg`.
//
// ORG-016 IS FIRST, and it is the reason this file exists. A duplicate external
// reference is the one refusal a super_admin provisioning an organization will
// actually hit — CITS's CRM key is typed by hand — and the operator can fix it
// without losing the other ten fields, so it attaches INLINE to the External
// reference ID field and carries the server's own code beside the copy.
//
// THE CODE IS RENDERED, NEVER ASSUMED. ORG-016 was ORG-006 in the epics table until
// this story corrected it (ORG-006 has been `invitation_rate_limited` since Story
// 14.2). A banner that printed a hardcoded code would have printed the wrong one,
// and would need re-fixing on the next re-allocation.

/** Banner descriptor. `variant` maps 1:1 onto a `Banner` variant. */
export interface CreateOrganizationBanner {
  /** `info` = a settled state (no retry); `warn` = check/conflict; `danger` = refusal/transport. */
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
  /** The server's own error code, rendered verbatim beside the copy (AC14). */
  code?: string;
}

/** Inline messages keyed to the Screen 33 form's RHF field names. */
export interface CreateOrganizationFieldErrors {
  name?: string;
  externalReferenceId?: string;
  billingAddress?: string;
  seatCapacity?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  billingContactEmail?: string;
  securityContactEmail?: string;
  adminEmail?: string;
  adminFirstName?: string;
  adminLastName?: string;
}

export interface MappedCreateOrganizationError {
  /** Banner descriptor, or null when the failure is fully handled inline. */
  banner: CreateOrganizationBanner | null;
  /** Per-field messages to attach via `form.setError` without dropping form state. */
  fieldErrors: CreateOrganizationFieldErrors;
}

const GENERIC: CreateOrganizationBanner = {
  variant: "danger",
  title: "Something went wrong",
  // NOT "please try again" on its own. A request can fail on the way BACK — the
  // organization is created and the 201 is lost — and a retry on that state creates
  // a second one, or trips ORG-016 on the CRM key. Check the directory first.
  body: "We could not confirm whether the organization was created. Check the organization directory — search the external reference ID — before trying again.",
};

// ORG-016 — the CRM key already identifies an organization. Both halves are shown:
// the banner explains what happened and carries the code, and the inline message
// points at the single field the operator has to change.
const DUPLICATE_REFERENCE_MESSAGE =
  "That external reference ID already belongs to another organization.";

const DUPLICATE_REFERENCE_BANNER = {
  variant: "warn",
  title: "That external reference ID is already in use",
  body: "Another organization already carries this CRM key. Check the reference in CITS and enter the one for this organization — nothing has been created.",
} satisfies Omit<CreateOrganizationBanner, "code">;

// ORG-012 — the first org_admin's address already identifies an account. Inline on
// the address, for the same reason as ORG-016: one field is wrong, not the form.
const EMAIL_IN_USE_MESSAGE = "That email address is already in use by another account.";

// ORG-001 — the contract window is empty or backwards. The client blocks a MISSING
// date (AC13); the ordering rule is the server's, so its refusal lands here and is
// attached to the end date, which is the field the operator moves.
const INVALID_WINDOW_MESSAGE = "The end date must be after the start date.";

// 401 and an uncoded 403 are one user-visible situation — the invite surfaces answer
// an anonymous caller with 403 rather than 401, so keying "signed out" off 401 alone
// would report an expired session as a permanent capability refusal.
const SESSION_EXPIRED: CreateOrganizationBanner = {
  variant: "warn",
  title: "Your session may have expired",
  body: "Sign in again and retry this action.",
};

/**
 * True when the SERVER named the failure. `UNKNOWN` is `api-client`'s placeholder
 * for a response that carried no code at all, so it is not a code.
 */
function hasServerCode(error: ApiError): boolean {
  return Boolean(error.code) && error.code !== "UNKNOWN";
}

/** Translate one 422 detail item into our own field message. */
function applyDetailItem(item: unknown, into: CreateOrganizationFieldErrors): void {
  if (!item || typeof item !== "object") return;
  const loc = (item as { loc?: unknown }).loc;
  const field = Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : "";

  switch (field) {
    case "name":
      into.name ??= "Enter the legal organization name.";
      break;
    case "external_reference_id":
      into.externalReferenceId ??= "Enter the external reference ID supplied by CITS.";
      break;
    case "billing_address":
      into.billingAddress ??= "Enter the billing address.";
      break;
    case "seat_capacity":
      into.seatCapacity ??= "Enter a seat capacity of 1 or more.";
      break;
    // Both contract edges carry `le=MAX_CONTRACT_END_DATE`, and that refusal is
    // a schema-level 422 with no error code — `loc` is the only thing routing it
    // to a FIELD, and the copy below is the only place the bound is ever stated.
    // Neither date input carries a `max=`, deliberately: the server enforces,
    // the mapper explains. WHICH sentence to use is `type`'s job rather than
    // `loc`'s, and both forms share that decision in
    // `contract-date-bounds.ts` — a parse failure, a wrong type and an explicit
    // null land on the same field and are not range problems.
    //
    // The start gained its ceiling in the 15.6 review round; before that, a
    // start above the bound reached the command and came back as 400 ORG-001
    // against the END field, because a bounded end is necessarily below such a
    // start. The refusal now lands on the control that is actually out of range.
    case "contract_start_date":
      into.contractStartDate ??= contractDateMessage("start", item);
      break;
    case "contract_end_date":
      into.contractEndDate ??= contractDateMessage("end", item);
      break;
    case "billing_contact_email":
      into.billingContactEmail ??= "Enter a valid email address.";
      break;
    case "security_contact_email":
      into.securityContactEmail ??= "Enter a valid email address.";
      break;
    case "admin_email":
      into.adminEmail ??= "Enter a valid email address.";
      break;
    case "admin_first_name":
      into.adminFirstName ??= "Enter the first organization admin's first name.";
      break;
    case "admin_last_name":
      into.adminLastName ??= "Enter the first organization admin's last name.";
      break;
  }
}

function parse422Details(details: unknown): CreateOrganizationFieldErrors {
  const fieldErrors: CreateOrganizationFieldErrors = {};
  if (Array.isArray(details)) {
    for (const item of details) applyDetailItem(item, fieldErrors);
  }
  return fieldErrors;
}

/** Map any create-organization failure to inline field errors + an optional banner. */
export function toCreateOrganizationError(error: unknown): MappedCreateOrganizationError {
  if (error instanceof ApiError) {
    // --- Coded branches first: a specific code always beats a bare status. ---
    if (error.code === "ORG-016") {
      return {
        fieldErrors: { externalReferenceId: DUPLICATE_REFERENCE_MESSAGE },
        // The code comes off the RESPONSE, not a literal. This branch is keyed on
        // ORG-016 so the two agree today, but the epics table said ORG-006 until
        // this story corrected it — reading it back is what keeps the rendered
        // support handle honest through the next re-allocation.
        banner: { ...DUPLICATE_REFERENCE_BANNER, code: error.code },
      };
    }
    if (error.code === "ORG-012") {
      return { fieldErrors: { adminEmail: EMAIL_IN_USE_MESSAGE }, banner: null };
    }
    if (error.code === "ORG-001") {
      return { fieldErrors: { contractEndDate: INVALID_WINDOW_MESSAGE }, banner: null };
    }
    // ORG-015 — the address parsed as RFC 5322 but failed the narrower pattern the
    // platform signs in with. Inline on the address it was raised for.
    if (error.code === "ORG-015") {
      return {
        fieldErrors: { adminEmail: "Enter a valid email address." },
        banner: null,
      };
    }

    if (error.status === 422) {
      const fieldErrors = parse422Details(error.details);
      const handledInline = Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors,
        // Only banner when nothing could be attached to a field, so the operator is
        // never told "check the details" with no field actually flagged.
        banner: handledInline
          ? null
          : {
              variant: "warn",
              title: "Check the details",
              body: "Please review the fields and try again.",
            },
      };
    }

    // Only an UNCODED 403 joins 401 here, matching `admin-user-error.ts`. The
    // capability refusal this endpoint actually raises is uncoded —
    // `requires_capability` raises `InsufficientPermissionsException`, which ships
    // with no `error_code` (`application/common/exceptions.py`), so `api-client`
    // fills the literal `UNKNOWN` — and it is indistinguishable on the wire from
    // the anonymous caller the invite surfaces also answer with 403. "Sign in
    // again" is the honest remedy for both, and the route guard already keeps a
    // non-super_admin off this screen.
    //
    // A CODED 403 must NOT land here. The one this endpoint can reach is AUTH-018
    // (the account is deactivated); it and any code the server gains before this
    // file learns it fall through to the default branch, which names the server's
    // own code rather than blaming a session that was never the problem.
    if (error.status === 401 || (error.status === 403 && !hasServerCode(error))) {
      return { fieldErrors: {}, banner: SESSION_EXPIRED };
    }

    // Default — surface the server's OWN code verbatim. No translation table, so a
    // code this story does not own cannot be mis-explained. Status 0 is a transport
    // failure whose "code" is the client's own placeholder, and quoting it would
    // send an operator to support with a string the server never sent.
    if (error.status !== 0 && hasServerCode(error)) {
      return { fieldErrors: {}, banner: { ...GENERIC, code: error.code } };
    }
  }
  // NETWORK_ERROR / AuthExpiredError / unknown — nothing internal leaked.
  return { fieldErrors: {}, banner: GENERIC };
}
